import { t } from 'i18next';
import { camelCase, upperFirst } from 'lodash';

import { IManagedNote } from 'core/components';
import { NOTE_DEFAULT_MAX_LENGTH, NOTE_SUPSHIP_MAX_LENGTH } from 'quotes/constants';
import { QuoteEntityType, QuotePartNoteType, QuoteType } from 'quotes/enums';
import { ICustomerQuotePart, IQuotePart, IQuotePartNote } from 'quotes/types';
import { Color } from 'theme/enums';

export const getDefaultValues = (quoteParts: IQuotePart[], hasUndiscardableNote = false) => {
  if (!quoteParts.length && !hasUndiscardableNote) {
    return {};
  }

  const hasEmptyUndiscardableNotes = quoteParts.some(
    (part) =>
      getMappedNotesArrByType(part as ICustomerQuotePart, QuotePartNoteType.UNDISCARDABLE_ITEM)
        .length === 0
  );

  const shouldSetUndiscardableItem = hasUndiscardableNote || !hasEmptyUndiscardableNotes;

  return {
    undiscardableItem: shouldSetUndiscardableItem,
    notes: {
      [QuotePartNoteType.UNDISCARDABLE_ITEM]: {
        enabled: shouldSetUndiscardableItem,
      },
    },
  };
};

export const getAvailableNoteTypes = (
  isAlternateVersionEnabled = false,
  quoteType?: QuoteType
): QuotePartNoteType[] =>
  [
    QuotePartNoteType.UNDISCARDABLE_ITEM,
    QuotePartNoteType.DEFAULT,
    QuotePartNoteType.LEAD_TIME,
    // Seller-only: an unknown quote type withholds it, so only the customer side ever offers it.
    QuoteType.CUSTOMER === quoteType ? QuotePartNoteType.INTERNAL : null,
    isAlternateVersionEnabled ? QuotePartNoteType.BUYER : null,
    isAlternateVersionEnabled ? QuotePartNoteType.PICKER : null,
    isAlternateVersionEnabled ? QuotePartNoteType.SUPSHIP : null,
  ].filter(Boolean);

export const getNoteLabel = (type: QuotePartNoteType, withNoteSuffix = true) =>
  t(`labelCreate${upperFirst(camelCase(type))}${withNoteSuffix ? 'Note' : ''}`);

// INTERNAL deliberately has no branch of its own: it belongs to the seller-only family alongside
// buyer/picker/supship, so it shares their green instead of getting a unique colour.
export const getNoteCategoryColor = (type: QuotePartNoteType) =>
  [QuotePartNoteType.DEFAULT, QuotePartNoteType.LEAD_TIME].includes(type)
    ? Color.PRIMARY
    : Color.SUCCESS;

// Shared by the note card's warning icon and the add form's helper line, so both read one copy.
export const getNoteInfoTooltip = (type: QuotePartNoteType): string | undefined =>
  QuotePartNoteType.INTERNAL === type ? t('tooltipInternalNoteInfo') : undefined;

export const getNoteSx = (type: QuotePartNoteType) =>
  [
    QuotePartNoteType.DEFAULT,
    QuotePartNoteType.LEAD_TIME,
    QuotePartNoteType.UNDISCARDABLE_ITEM,
  ].includes(type)
    ? { backgroundColor: 'primary.50' }
    : { backgroundColor: 'success.50' };

export const getNoteMaxLength = (type: QuotePartNoteType): number | undefined => {
  switch (type) {
    case QuotePartNoteType.BUYER:
    case QuotePartNoteType.PICKER:
      return NOTE_DEFAULT_MAX_LENGTH;
    case QuotePartNoteType.SUPSHIP:
      return NOTE_SUPSHIP_MAX_LENGTH;
    default:
      return undefined;
  }
};

export const mapQuotePartNotesToManagedNotes = (
  notes: IQuotePartNote[],
  isAlternateVersionEnabled = false,
  entityType?: QuoteEntityType
) =>
  notes.map((note) => ({
    ...note,
    isEditable: true,
    isDeletable: true,
    maxLength:
      isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType
        ? getNoteMaxLength(note.type)
        : undefined,
    category:
      note.type !== QuotePartNoteType.UNDISCARDABLE_ITEM
        ? {
            label: getNoteLabel(note.type, false),
            color: getNoteCategoryColor(note.type),
            infoTooltip: getNoteInfoTooltip(note.type),
          }
        : undefined,
    sx: getNoteSx(note.type),
  }));

export const getMappedNotesArrByType = (
  data: ICustomerQuotePart,
  type: QuotePartNoteType,
  entityType?: QuoteEntityType
): IManagedNote[] =>
  mapQuotePartNotesToManagedNotes(
    Object.values(data?.notes || {}).filter((note) => note.type === type) || [],
    !!data?.alternateVersionEnabled,
    entityType
  )
    .filter((note) => note.type === type)
    .reverse();
